import React, { useEffect, useState } from 'react'

interface PreloaderProps {
    onComplete: () => void
}

const Preloader: React.FC<PreloaderProps> = ({ onComplete }) => {
    const [phase, setPhase] = useState<'assembling' | 'scanning' | 'exiting'>('assembling')
    const [progress, setProgress] = useState(0)

    useEffect(() => {
        // Phase 1: Shield assembles over 1.8s, progress bar fills
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(progressInterval)
                    return 100
                }
                return prev + 1.2
            })
        }, 22)

        // Phase 2: Scan line
        const scanTimer = setTimeout(() => setPhase('scanning'), 1900)

        // Phase 3: Exit
        const exitTimer = setTimeout(() => setPhase('exiting'), 2700)

        // Done — unmount
        const doneTimer = setTimeout(() => onComplete(), 3400)

        return () => {
            clearInterval(progressInterval)
            clearTimeout(scanTimer)
            clearTimeout(exitTimer)
            clearTimeout(doneTimer)
        }
    }, [onComplete])

    return (
        <div className={`preloader-root ${phase === 'exiting' ? 'preloader-exit' : ''}`}>
            {/* Background grid */}
            <div className="preloader-grid" />

            {/* Center stage */}
            <div className="preloader-center">

                {/* Shield SVG */}
                <div className={`shield-wrapper ${phase !== 'assembling' ? 'shield-assembled' : ''}`}>
                    <svg
                        viewBox="0 0 120 140"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shield-svg"
                        aria-hidden="true"
                    >
                        <defs>
                            <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#d81d1d" />
                                <stop offset="100%" stopColor="#e90e0e" />
                            </linearGradient>
                            <filter id="glow">
                                <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>

                        {/* Shield outline — stroked path only, drawn in */}
                        <path
                            className="shield-outline"
                            d="M60 6 L108 24 L108 70 C108 100 60 134 60 134 C60 134 12 100 12 70 L12 24 Z"
                            fill="none"
                            stroke="url(#shieldGrad)"
                            strokeWidth="2.5"
                            filter="url(#glow)"
                        />

                        {/* Inner segments — each fades/slides in with delay */}
                        <path
                            className="shield-seg seg-1"
                            d="M60 20 L98 34 L98 70 C98 92 60 120 60 120 C60 120 22 92 22 70 L22 34 Z"
                            fill="url(#shieldGrad)"
                            opacity="0.08"
                        />

                        {/* Horizontal divider lines inside shield */}
                        <line className="shield-line line-1" x1="24" y1="60" x2="96" y2="60"
                            stroke="#f63b3b" strokeWidth="0.8" opacity="0.4" />
                        <line className="shield-line line-2" x1="30" y1="80" x2="90" y2="80"
                            stroke="#f63b3b" strokeWidth="0.8" opacity="0.4" />

                        {/* Center G mark */}
                        <text
                            className="shield-letter"
                            x="60" y="82"
                            textAnchor="middle"
                            fontFamily="Georgia, serif"
                            fontSize="38"
                            fontWeight="700"
                            fill="url(#shieldGrad)"
                            filter="url(#glow)"
                        >
                            G
                        </text>

                        {/* Corner accent dots */}
                        <circle className="shield-dot dot-tl" cx="30" cy="34" r="2" fill="#fa6060" />
                        <circle className="shield-dot dot-tr" cx="90" cy="34" r="2" fill="#fa6060" />
                        <circle className="shield-dot dot-bl" cx="30" cy="100" r="2" fill="#fa6060" />
                        <circle className="shield-dot dot-br" cx="90" cy="100" r="2" fill="#fa6060" />
                    </svg>

                    {/* Scan line overlay */}
                    {phase === 'scanning' && (
                        <div className="scan-line" />
                    )}
                </div>

                {/* Wordmark */}
                <div className={`preloader-wordmark ${phase === 'assembling' ? '' : 'wordmark-visible'}`}>
                    <span className="wordmark-main">GUARDRISK</span>
                    <span className="wordmark-sub">MORE THAN PROTECTION</span>
                </div>

                {/* Progress bar */}
                <div className="preloader-progress-track">
                    <div
                        className="preloader-progress-fill"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                </div>

            </div>

            <style>{`
                /* ── Root ── */
                .preloader-root {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    background: #180606;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    transition: opacity 0.6s ease, transform 0.6s ease;
                }
                .preloader-exit {
                    opacity: 0;
                    transform: translateY(-8px);
                    pointer-events: none;
                }

                /* ── Grid background ── */
                .preloader-grid {
                    position: absolute;
                    inset: 0;
                    background-image:
                        linear-gradient(rgba(246, 59, 59, 0.06) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(246, 59, 59, 0.06) 1px, transparent 1px);
                    background-size: 40px 40px;
                    mask-image: radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 100%);
                }

                /* ── Center layout ── */
                .preloader-center {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 2rem;
                }

                /* ── Shield ── */
                .shield-wrapper {
                    position: relative;
                    width: 160px;
                    height: 186px;
                }
                .shield-svg {
                    width: 100%;
                    height: 100%;
                    overflow: visible;
                }

                /* Outline draws itself in */
                .shield-outline {
                    stroke-dasharray: 420;
                    stroke-dashoffset: 420;
                    animation: draw-outline 1.4s cubic-bezier(0.4,0,0.2,1) 0.1s forwards;
                }
                @keyframes draw-outline {
                    to { stroke-dashoffset: 0; }
                }

                /* Inner fill fades in */
                .shield-seg {
                    opacity: 0;
                    animation: fade-in-seg 0.6s ease 1.2s forwards;
                }
                @keyframes fade-in-seg {
                    to { opacity: 0.12; }
                }

                /* Lines draw from center */
                .shield-line {
                    stroke-dasharray: 80;
                    stroke-dashoffset: 80;
                }
                .line-1 { animation: draw-line 0.4s ease 1.1s forwards; }
                .line-2 { animation: draw-line 0.4s ease 1.25s forwards; }
                @keyframes draw-line {
                    to { stroke-dashoffset: 0; }
                }

                /* Letter rises in */
                .shield-letter {
                    opacity: 0;
                    transform: translateY(6px);
                    transform-origin: 60px 70px;
                    animation: rise-letter 0.5s cubic-bezier(0.2,0.8,0.2,1) 1.35s forwards;
                }
                @keyframes rise-letter {
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Dots pop in */
                .shield-dot {
                    opacity: 0;
                    transform: scale(0);
                    transform-box: fill-box;
                    transform-origin: center;
                }
                .dot-tl { animation: pop-dot 0.3s cubic-bezier(0.2,1.5,0.4,1) 1.0s forwards; }
                .dot-tr { animation: pop-dot 0.3s cubic-bezier(0.2,1.5,0.4,1) 1.05s forwards; }
                .dot-bl { animation: pop-dot 0.3s cubic-bezier(0.2,1.5,0.4,1) 1.1s forwards; }
                .dot-br { animation: pop-dot 0.3s cubic-bezier(0.2,1.5,0.4,1) 1.15s forwards; }
                @keyframes pop-dot {
                    to { opacity: 1; transform: scale(1); }
                }

                /* Assembled: gentle pulse */
                .shield-assembled .shield-outline {
                    animation: draw-outline 1.4s cubic-bezier(0.4,0,0.2,1) 0.1s forwards,
                                glow-pulse 2s ease-in-out 1.6s infinite alternate;
                }
                @keyframes glow-pulse {
                    from { filter: drop-shadow(0 0 4px #f63b3b80); }
                    to   { filter: drop-shadow(0 0 12px #f63b3bcc); }
                }

                /* ── Scan line ── */
                .scan-line {
                    position: absolute;
                    left: 5%;
                    right: 5%;
                    height: 2px;
                    background: linear-gradient(90deg, transparent, #fc7d7d, #f83838, #fc7d7d, transparent);
                    top: 0;
                    animation: scan 0.65s cubic-bezier(0.4,0,0.6,1) forwards;
                    box-shadow: 0 0 12px 4px #f62828;
                    border-radius: 2px;
                }
                @keyframes scan {
                    from { top: 0; opacity: 1; }
                    to   { top: 100%; opacity: 0.4; }
                }

                /* ── Wordmark ── */
                .preloader-wordmark {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.4rem;
                    opacity: 0;
                    transform: translateY(10px);
                    transition: opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s;
                }
                .wordmark-visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                .wordmark-main {
                    font-family: 'Georgia', serif;
                    font-size: 1.6rem;
                    font-weight: 700;
                    letter-spacing: 0.25em;
                    color: #e8f0ff;
                }
                .wordmark-sub {
                    font-family: Arial, sans-serif;
                    font-size: 0.62rem;
                    letter-spacing: 0.3em;
                    color: #d70b0b;
                    text-transform: uppercase;
                }

                /* ── Progress bar ── */
                .preloader-progress-track {
                    width: 180px;
                    height: 1px;
                    background: rgb(192, 188, 188);
                    border-radius: 4px;
                    overflow: hidden;
                }
                .preloader-progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #f30f0f, #f39191);
                    border-radius: 4px;
                    transition: width 0.06s linear;
                    box-shadow: 0 0 8px #38bdf890;
                }
            `}</style>
        </div>
    )
}

export default Preloader